import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth, ROLE_LABEL } from "@/context/AuthContext";
import { LockButton } from "@/components/LockButton";
import UndoButton from "@/components/UndoButton";
import { Home, ClipboardList, ClipboardCheck, ListChecks, AlertTriangle, User, Bell, LogOut, LayoutDashboard, Euro, PackageX, Backpack, Calendar, CalendarClock, BedDouble, Warehouse, Building2, Users, Package, PackageMinus, Search, UserCircle, ArrowLeftRight, Menu, Truck, Sparkles, Shirt, Route, Timer, FileText, Banknote, Receipt, Video, Snowflake, Trash2, Percent, KeyRound } from "lucide-react";
import { RefillBanner } from "@/components/RefillBanner";
import { Button } from "@/components/ui/button";
import { BackButton } from "@/components/BackButton";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SortableNav } from "@/components/layout/SortableNav";
import { AdminAreaNav } from "@/components/layout/AdminAreaNav";
import { EscBackButton } from "@/components/layout/EscBackButton";
import { useBilingual } from "@/lib/useBilingual";

const LOGO_URL = "/kilolava-logo.png";

// I label degli item operatore/rider hanno una `labelKey` per la traduzione bilingue.
// Le pagine admin restano sempre in italiano (l'admin non ha `preferred_language`).
const EMPLOYEE_NAV = [
  { to: "/oggi", label: "Oggi", labelKey: "nav_today", icon: Home, testId: "nav-oggi" },
  { to: "/pulizie", label: "Pulizie", labelKey: "nav_cleanings", icon: ClipboardList, testId: "nav-pulizie" },
  { to: "/kilo", label: "Kilo AI", labelKey: "nav_kilo", icon: Sparkles, testId: "nav-kilo" },
  { to: "/segnalazioni", label: "Segnalazioni", labelKey: "nav_reports", icon: AlertTriangle, testId: "nav-segnalazioni" },
  { to: "/profilo", label: "Profilo", labelKey: "nav_profile", icon: User, testId: "nav-profilo" },
];

// Bottom nav on mobile: keep 4 items only (Kilo lives in the drawer/desktop sidebar).
const EMPLOYEE_BOTTOM_NAV = EMPLOYEE_NAV.filter((n) => n.to !== "/kilo");

// Extra items visible to responsabile (Diana): oversight of the whole team.
// La voce "Pulizie" del responsabile è la Scheda quotidiana in sola lettura (vedi sotto).
const MANAGER_SCHEDULE_NAV = { to: "/admin/schedule", label: "Pulizie", icon: Calendar, testId: "nav-manager-schedule" };

const MANAGER_EXTRA_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-manager-dashboard" },
  { to: "/admin/gestione", label: "Gestione", icon: ClipboardCheck, testId: "nav-manager-gestione" },
  { to: "/admin/rientri", label: "Rientri", icon: KeyRound, testId: "nav-manager-rientri" },
  { to: "/admin/programmazione", label: "Programmazione", icon: CalendarClock, testId: "nav-manager-programmazione" },
  { to: "/admin/reports", label: "Tutte le segnalazioni", icon: AlertTriangle, testId: "nav-manager-reports" },
  { to: "/admin/storico", label: "Storico", icon: ArrowLeftRight, testId: "nav-manager-history" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/gestione", label: "Gestione", icon: ClipboardCheck, testId: "nav-admin-gestione" },
  { to: "/admin/rientri", label: "Rientri", icon: KeyRound, testId: "nav-admin-rientri" },
  { to: "/admin/kilo", label: "Kilo AI", icon: Sparkles, testId: "nav-admin-kilo" },
  { to: "/admin/schedule", label: "Pulizie", icon: Calendar, testId: "nav-admin-schedule" },
  { to: "/admin/programmazione", label: "Programmazione", icon: CalendarClock, testId: "nav-admin-programmazione" },
  { to: "/admin/structures", label: "Strutture", icon: Building2, testId: "nav-admin-structures" },
  { to: "/admin/inventory", label: "Magazzinetti", icon: Warehouse, testId: "nav-admin-inventory" },
  { to: "/admin/magazzino-sede", label: "Magazzino sede", icon: Warehouse, testId: "nav-admin-hq" },
  { to: "/admin/lavanderia", label: "Lavanderia privata", icon: Shirt, testId: "nav-admin-lavanderia" },
  { to: "/logistica", label: "Consegne", icon: Truck, testId: "nav-admin-logistica" },
  { to: "/admin/giri", label: "Giri (mappa)", icon: Route, testId: "nav-admin-giri" },
  { to: "/admin/storico", label: "Storico", icon: ArrowLeftRight, testId: "nav-admin-history" },
  { to: "/admin/products", label: "Prodotti", icon: Package, testId: "nav-admin-products" },
  { to: "/admin/kits", label: "Kit", icon: Package, testId: "nav-admin-kits" },
  { to: "/admin/access-search", label: "Access-search", icon: Search, testId: "nav-admin-access" },
  { to: "/admin/users", label: "Utenti", icon: Users, testId: "nav-admin-users" },
  { to: "/admin/tasklist", label: "Task list", icon: ListChecks, testId: "nav-admin-tasklist" },
  { to: "/admin/note-specifiche", label: "Specifiche note", icon: Bell, testId: "nav-admin-note-specifiche" },
  { to: "/admin/stagioni", label: "Estate / Inverno", icon: Snowflake, testId: "nav-admin-stagioni" },
  { to: "/admin/riposi", label: "Riposo dipendenti", icon: BedDouble, testId: "nav-admin-riposi" },
  { to: "/admin/clienti", label: "Clienti", icon: UserCircle, testId: "nav-admin-clients" },
  { to: "/admin/non-conformi", label: "Non conformi", icon: PackageX, testId: "nav-admin-storni" },
  { to: "/admin/analisi-storni", label: "Statistiche non conformi", icon: PackageX, testId: "nav-admin-analisi-storni" },
  { to: "/admin/ddt", label: "DDT", icon: FileText, testId: "nav-admin-ddt" },
  { to: "/admin/standard", label: "Preventivi e contratti", icon: FileText, testId: "nav-admin-standard" },
  { to: "/admin/stato-video", label: "Stato video", icon: Video, testId: "nav-admin-stato-video" },
  { to: "/admin/setup", label: "Setup da completare", icon: ClipboardCheck, testId: "nav-admin-setup" },
  { to: "/admin/prenotazioni", label: "Prenotazioni", icon: CalendarClock, testId: "nav-admin-prenotazioni" },
  { to: "/admin/storni-dipendenti", label: "Non conformi dipendenti", icon: Percent, testId: "nav-admin-storni-dipendenti" },
  { to: "/admin/ore-dipendenti", label: "Ore dipendenti", icon: Timer, testId: "nav-admin-ore-dipendenti" },
  { to: "/admin/cestino", label: "Cestino", icon: Trash2, testId: "nav-admin-trash" },
  { to: "/admin/azienda", label: "Dati azienda", icon: Building2, testId: "nav-admin-azienda" },
  { to: "/admin/materiale", label: "Magazzino pulizia", icon: Backpack, testId: "nav-admin-materiale" },
  { to: "/admin/report-tempi", label: "Report tempi", icon: Timer, testId: "nav-admin-report-tempi" },
  { to: "/admin/economics", label: "Economics", icon: Euro, testId: "nav-admin-economics" },
  { to: "/admin/tassa-soggiorno", label: "Tassa di soggiorno", icon: Banknote, testId: "nav-admin-tassa" },
  { to: "/admin/chiusura-mese", label: "Chiusura mese", icon: FileText, testId: "nav-admin-chiusura" },
  { to: "/admin/fatturazione", label: "Fatturazione", icon: Receipt, testId: "nav-admin-fatturazione" },
];

// Nav dedicata al Responsabile Logistica: consegne + strutture (accessi) + profilo.
const LOGISTICS_NAV = [
  { to: "/logistica", label: "Consegne", labelKey: "nav_logistics", icon: Package, testId: "nav-logistica-home" },
  { to: "/logistica/strutture", label: "Strutture", labelKey: "nav_logistics_structures", icon: Building2, testId: "nav-logistica-strutture" },
  { to: "/admin/non-conformi", label: "Non conformi", icon: PackageX, testId: "nav-logistica-storni" },
  { to: "/profilo", label: "Profilo", labelKey: "nav_profile", icon: User, testId: "nav-logistica-profilo" },
];

export function AppLayout({ variant = "employee" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { bt, dir } = useBilingual();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Helper: True se l'utente ha il ruolo (primario O extra).
  const hasRole = (r) => user?.role === r || (user?.extra_roles || []).includes(r);
  const isSuperAdmin = hasRole("super_admin");
  const isManager = hasRole("responsabile");
  const isEmployee = hasRole("dipendente");
  const isLogistica = hasRole("resp_logistica") || hasRole("rider");
  // Il layout style dipende dal RUOLO effettivo, non dalla route.
  // Solo il super_admin vede la sidebar amministrativa "Kilolava Operations".
  // Responsabile, logistica, rider e dipendente vedono sempre la sidebar operatore
  // anche quando visitano route `/admin/*` (a cui hanno accesso in lettura).
  const isAdmin = isSuperAdmin;
  // `variant` prop retained for backwards compatibility ma ignorato per ruoli non-super.
  void variant;
  // Nav dinamica: componiamo l'unione dei link in base ai ruoli dell'utente.
  // Super_admin vede sempre ADMIN_NAV (accesso completo). Per gli altri
  // uniamo employee (con extra manager) e logistics se ha i ruoli relativi.
  let navItems;
  if (isSuperAdmin) {
    navItems = ADMIN_NAV;
  } else {
    const parts = [];
    // Per il responsabile la voce "Pulizie" diventa la Scheda quotidiana (sola lettura).
    if (isEmployee || isManager) {
      parts.push(...EMPLOYEE_NAV.map((it) => (it.to === "/pulizie" && isManager ? MANAGER_SCHEDULE_NAV : it)));
    }
    if (isManager) parts.push(...MANAGER_EXTRA_NAV);
    if (isLogistica) parts.push(...LOGISTICS_NAV);
    // Dedup per `to`
    const seen = new Set();
    navItems = parts.filter((it) => {
      if (seen.has(it.to)) return false;
      seen.add(it.to);
      return true;
    });
    // Fallback: se non ha match, default employee nav
    if (navItems.length === 0) navItems = EMPLOYEE_NAV;
  }
  // Applica traduzione bilingue (IT · Traduzione) ai label operatore.
  // Solo se il ruolo primario NON è super_admin (l'admin resta in italiano).
  if (!isSuperAdmin) {
    navItems = navItems.map((it) => (it.labelKey ? { ...it, label: bt(it.labelKey) } : it));
  }
  // Layout shell (bottom nav + notifications bell): mostrata per chi non è super_admin.
  const showEmployeeShell = !isAdmin || isManager || isLogistica;

  // Bilingual bottom nav items (mobile) — sempre operatore, mai admin.
  // Il responsabile ha la Scheda quotidiana al posto di "Pulizie".
  const bottomNavItems = EMPLOYEE_BOTTOM_NAV
    .map((it) => (it.to === "/pulizie" && isManager && !isEmployee ? MANAGER_SCHEDULE_NAV : it))
    .map((it) => (it.labelKey ? { ...it, label: bt(it.labelKey) } : it));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Close mobile menu when route changes
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#1F1F1F]" dir={dir}>
      {/* Top bar (mobile) */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-3 h-14 flex items-center justify-between md:hidden">
        <div className="flex items-center gap-1">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 -ml-2"
                aria-label="Apri menu"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-menu-panel">
              <div className="p-6 border-b border-gray-200">
                <img src={LOGO_URL} alt="Kilolava" className="h-9 w-auto" />
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mt-2">Operations</div>
              </div>
              {isSuperAdmin ? (
                <AdminAreaNav onNavigate={() => setMobileOpen(false)} />
              ) : (
                <SortableNav
                  items={navItems}
                  canReorder={false}
                  isMobile
                  onNavigate={() => setMobileOpen(false)}
                />
              )}
              <div className="p-4 border-t border-gray-200">
                <div className="mb-3 text-sm">
                  <div className="font-semibold">{user?.full_name}</div>
                  <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                  <div className="mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-gray-100 text-gray-700" data-testid="sidebar-role-badge">
                    {ROLE_LABEL[user?.actual_role || user?.role] || user?.role}
                  </div>
                </div>
                {isAdmin && <div className="mb-2"><LockButton testId="lock-btn-mobile" /></div>}
                <Button variant="outline" size="sm" onClick={handleLogout} className="w-full h-11 rounded-xl" data-testid="mobile-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> {bt("logout")}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link">
            <img src={LOGO_URL} alt="Kilolava" className="h-8 w-auto" />
          </Link>
        </div>
        <div className="flex items-center gap-1">
          <BackButton testId="back-btn-mobile" />
          {showEmployeeShell && (
            <Link to="/notifiche" className="p-2 rounded-full hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center" data-testid="notifications-link-mobile">
              <Bell className="w-5 h-5" />
            </Link>
          )}
        </div>
      </header>

      <div className="flex">
        {/* Sidebar (desktop) */}
        {isAdmin ? (
          <aside className="hidden md:flex md:flex-col w-64 min-h-screen bg-white border-r border-gray-200 sticky top-0 h-screen">
            <div className="p-6 border-b border-gray-200">
              <Link to={isManager ? "/oggi" : "/admin/home"} className="flex items-center gap-3" data-testid="sidebar-logo-link">
                <img src={LOGO_URL} alt="Kilolava" className="h-9 w-auto" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Operations</span>
              </Link>
            </div>
            <AdminAreaNav />
            <div className="p-4 border-t border-gray-200">
              <div className="mb-3 text-sm">
                <div className="font-semibold text-[#1F1F1F]">{user?.full_name}</div>
                <div className="text-xs text-gray-500">{user?.email}</div>
                <div className="mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-gray-100 text-gray-700" data-testid="sidebar-role-badge-mobile">
                  {ROLE_LABEL[user?.actual_role || user?.role] || user?.role}
                </div>
              </div>
              <div className="mb-2"><LockButton /></div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="sidebar-logout-btn"
                className="w-full h-11 rounded-xl"
              >
                <LogOut className="w-4 h-4 mr-2" /> Esci
              </Button>
            </div>
          </aside>
        ) : (
          <aside className="hidden md:flex md:flex-col w-64 min-h-screen bg-white border-r border-gray-200 sticky top-0 h-screen">
            <div className="p-6 border-b border-gray-200">
              <Link to="/oggi" className="flex items-center gap-3" data-testid="sidebar-logo-link-employee">
                <img src={LOGO_URL} alt="Kilolava" className="h-9 w-auto" />
              </Link>
            </div>
            <nav className="flex-1 p-4 space-y-1">
              {navItems.map((n) => (
                <NavLink
                  key={n.testId || n.to}
                  to={n.to}
                  data-testid={n.testId + "-desktop"}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? "bg-[#EC4E5B] text-white shadow-sm"
                        : "text-gray-700 hover:bg-gray-100"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-gray-200">
              <div className="mb-3 text-sm">
                <div className="font-semibold text-[#1F1F1F]">{user?.full_name}</div>
                <div className="text-xs text-gray-500">{user?.email}</div>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="sidebar-logout-btn-employee" className="w-full h-11 rounded-xl">
                <LogOut className="w-4 h-4 mr-2" /> {bt("logout")}
              </Button>
            </div>
          </aside>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 pb-24 md:pb-8">
          <div className="hidden md:flex justify-end items-center gap-2 px-6 pt-4 -mb-2">
            {isSuperAdmin && <UndoButton />}
            {isSuperAdmin ? <EscBackButton /> : <BackButton testId="back-btn-desktop" />}
          </div>
          {isSuperAdmin && <div className="md:hidden flex justify-end items-center gap-2 px-3 pt-2"><UndoButton compact /><EscBackButton /></div>}
          {isSuperAdmin && <RefillBanner />}
          <Outlet />
        </main>
      </div>

      {/* Bottom nav (mobile) */}
      {showEmployeeShell && (
        <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 flex md:hidden" data-testid="bottom-nav">
          {bottomNavItems.map((n) => (
            <NavLink
              key={n.testId || n.to}
              to={n.to}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center py-2.5 min-h-[56px] gap-1 text-[10px] font-medium transition-colors ${
                  isActive ? "text-[#EC4E5B]" : "text-gray-500"
                }`
              }
            >
              <n.icon className="w-5 h-5" />
              <span className="text-center leading-tight px-1">{n.label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
